import { ChangeDetectionStrategy, Component, EventEmitter, Input, Optional, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, InputObservable } from '@y42/angular/util';
import { ConfirmationDialogModel, ConfirmDialogComponent } from '@y42/core/dialog/confirm-dialog';
import { areArraysEqual } from '@y42/util/are-arrays-equal';
import { showLoaderFor } from '@y42/util/show-loader-for-x-ms';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { ChatRedirectionHelperService } from '../helpers/chat-redirection-helper.service';
import {
  ChatRoomCreationStrategy,
  ChatRoomMessageUIModel,
  ChatRoomMessageUIPayload,
  ChatRoomUIModel,
} from '../models/chat-room.model';
import { ChatService } from '../services/chat.service';

interface MultipleStrategyIds {
  id: ChatRoomUIModel['id'] | undefined;
  uiId: string | undefined;
}

@UntilDestroy()
@Component({
  selector: 'y42-chat-single-room',
  templateUrl: './chat-single-room.component.html',
  styleUrls: ['./chat-single-room.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatSingleRoomComponent {
  constructor(
    private readonly chatService: ChatService,
    private readonly dialog: MatDialog,
    @Optional() private readonly chatRedirectionHelperService?: ChatRedirectionHelperService,
  ) {
    this.uiIdToRealIdEmitOnChange();
  }

  @Input()
  @InputObservable()
  chatRoomKey: string | undefined = undefined;
  private readonly chatRoomKey$!: Observable<string | undefined>;

  @Input()
  @InputObservable()
  chatRoomTarget: string | undefined = undefined;
  private readonly chatRoomTarget$!: Observable<string | undefined>;

  @Input()
  @InputObservable()
  chatRoomStrategy: ChatRoomCreationStrategy | undefined = undefined;
  private readonly chatRoomStrategy$!: Observable<ChatRoomCreationStrategy | undefined>;

  @Input()
  @InputObservable()
  multipleStrategyIds?: MultipleStrategyIds = undefined;
  private readonly multipleStrategyIds$!: Observable<MultipleStrategyIds | undefined>;

  @Input()
  relatedGitEntityPath?: string;

  @Output()
  chatResolved = new EventEmitter<ChatRoomUIModel>();

  @Output()
  uiIdToRealId = new EventEmitter<{ uiId: string; id: string }>();

  private readonly chatRoom$: Observable<ChatRoomUIModel | undefined> = combineLatest([
    this.chatRoomKey$,
    this.chatRoomTarget$,
    this.chatRoomStrategy$,
  ]).pipe(
    distinctUntilChanged((a, b) => areArraysEqual(a || [], b || [])),
    switchMap(([key, target, strategy]) => {
      const chatRooms$: Observable<ChatRoomUIModel[]> =
        key && target && strategy ? this.chatService.getChatRoomsFor({ key, target, strategy }) : of([]);

      return combineLatest([chatRooms$, this.multipleStrategyIds$]).pipe(
        map(([chatRooms, multipleStrategyIds]) => {
          if (strategy === 'singleton') {
            return chatRooms?.[0];
          } else if (strategy === 'multiple') {
            return (
              chatRooms.find((chatRoom) => chatRoom.id === multipleStrategyIds?.id) ||
              chatRooms.find((chatRoom) => chatRoom.metadata?.uiId === multipleStrategyIds?.uiId)
            );
          }

          return undefined;
        }),
      );
    }),
    startWith(undefined),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private readonly chatRoomConversation$ = this.chatRoom$.pipe(map((chatRoom) => chatRoom?.conversation || []));

  private readonly isResolveable$ = combineLatest([this.chatRoom$, this.chatRoomConversation$]).pipe(
    map(([chatRoom, chatRoomConversation]) => !!chatRoom?.id && !!chatRoomConversation.length),
  );

  public readonly state = connectState(this, {
    chatRoom: this.chatRoom$,
    isLoading: this.chatRoom$.pipe(
      map((chatRoom) => (chatRoom === undefined ? false : !!chatRoom.loading)),
      startWith(true),
      showLoaderFor(500),
      distinctUntilChanged(),
    ),
    chatRoomConversation: this.chatRoomConversation$,
    chatAuthor: this.chatService.chatAuthor$,
    mentionUsers: this.chatService.mentionUsers$,
    isResolveable: this.isResolveable$,
    moveToMessageId: this.chatRoom$.pipe(
      filter((chatRoom) => !!chatRoom),
      switchMap(
        (chatRoom) =>
          this.chatRedirectionHelperService?.chatRoomConfigToDisplay$.pipe(
            map((config) => ({ ...config, chatRoom })),
          ) || of({ chatRoomId: undefined, chatMessageId: undefined, chatRoom }),
      ),
      map((config) => {
        if (config.chatRoom && config.chatRoom.id === config.chatRoomId) {
          return { moveToMessageId: config.chatMessageId, shouldReset: true, config };
        }
        return { moveToMessageId: undefined, shouldReset: false, config };
      }),
      tap(({ moveToMessageId, shouldReset }) => {
        // second part of if statement to prevent infinite loop
        if (moveToMessageId === undefined && shouldReset) {
          // we won't have anything to consume, so let's just reset it in higher context
          this.onMoveToMessageIdConsumed();
        }
      }),
      map(({ moveToMessageId }) => moveToMessageId),
    ),
  });

  async onChatRoomCommented(msg: ChatRoomMessageUIPayload) {
    if (!this.chatRoomKey || !this.chatRoomTarget || !this.chatRoomStrategy) {
      // TODO: errorHandler -> config not defined
      return;
    }

    const chatRoomConfig = this.state.chatRoom?.id
      ? { id: this.state.chatRoom.id }
      : {
          config: {
            key: this.chatRoomKey,
            target: this.chatRoomTarget,
            strategy: this.chatRoomStrategy,
          },
          metadata: {
            uiMultipleChatRoomId: this.chatRoomStrategy === 'multiple' ? this.multipleStrategyIds?.uiId : undefined,
            relatedGitEntityPath: this.relatedGitEntityPath,
          },
        };
    await this.chatService.createMessageFor(chatRoomConfig, msg);
  }

  async onChatRoomMessageEdited(editedConfig: {
    messageId: ChatRoomMessageUIModel['id'];
    editedMessage: ChatRoomMessageUIPayload;
  }) {
    const chatRoomId = this.state.chatRoom?.id;
    if (!chatRoomId) {
      return;
    }
    await this.chatService.editMessageFor(chatRoomId, editedConfig.messageId, editedConfig.editedMessage);
  }

  async onChatRoomMessageDeleted(id: ChatRoomMessageUIModel['id']) {
    const chatRoomId = this.state.chatRoom?.id;
    if (!chatRoomId) {
      return;
    }

    await this.chatService.deleteMessageIn(chatRoomId, id);
  }

  async onChatRoomResolved() {
    const chatRoom = this.state.chatRoom;
    const chatRoomId = chatRoom?.id;
    if (!chatRoomId) {
      return;
    }

    const data = new ConfirmationDialogModel({
      header_text: 'Are you sure you want to resolve the conversation?',
      text: `You won't be able to retrieve the comments`,
      confirmation_text: 'Confirm',
      data: true,
    });

    this.dialog
      .open(ConfirmDialogComponent, {
        width: '600px',
        data,
        panelClass: ['confirmation-pop-up'],
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.chatService.resolveChatRoom(chatRoomId)),
        tap((succeeded: boolean) => {
          if (succeeded && chatRoom) {
            this.chatResolved.emit(chatRoom);
          }
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  onMoveToMessageIdConsumed() {
    this.chatRedirectionHelperService?.setChatRoomConfigToDisplay({
      chatRoomId: undefined,
      chatMessageId: undefined,
    });
  }

  private uiIdToRealIdEmitOnChange() {
    this.chatService.uiIdToRealId$
      .pipe(
        filter(({ uiId }) => !!this.multipleStrategyIds?.uiId && uiId === this.multipleStrategyIds?.uiId),
        tap((uiIdToId) => this.uiIdToRealId.emit(uiIdToId)),
        untilDestroyed(this),
      )
      .subscribe();
  }
}
